// Card "Mensagens recentes" na home, ao lado do card de Notificações.
// Mostra até 3 mensagens não-lidas; clique no item abre modal direto.

import { computed } from "@preact/signals";
import { useState } from "preact/hooks";
import { ChevronRight, Mail } from "lucide-react";
import { inboxState, MessageModal, setMessageRead } from "./MensagensView";
import type { InboxMessageRow } from "./api";

interface Props {
  onOpenAll: () => void;
}

const recent3UnreadMessages = computed(() =>
  inboxState.value.list.filter((m) => !m.readAt).slice(0, 3)
);

export function MensagensHomeCard({ onOpenAll }: Props) {
  const recent = recent3UnreadMessages.value;
  const totalUnread = inboxState.value.unreadCount;
  const isEmpty = recent.length === 0;
  const [openItem, setOpenItem] = useState<InboxMessageRow | null>(null);

  function openModal(m: InboxMessageRow) {
    setOpenItem(m);
    if (!m.readAt) void setMessageRead(m.id, true);
  }

  return (
    <section class="overflow-hidden rounded-md border border-blue-500/40 bg-blue-500/5 p-3 sm:p-4">
      <header class="mb-3 flex items-center justify-between gap-2">
        <h2 class="flex min-w-0 items-center gap-2 text-sm font-bold text-blue-700 dark:text-blue-400">
          <Mail className="size-4 shrink-0" />
          <span class="truncate">Mensagens recentes</span>
          {totalUnread > 0 && (
            <span class="inline-flex h-5 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-blue-500/15 px-1.5 text-[11px] font-bold tabular-nums text-blue-700 ring-1 ring-blue-500/25 dark:text-blue-400">
              {totalUnread}
            </span>
          )}
        </h2>
        <button
          type="button"
          onClick={onOpenAll}
          class="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-blue-700 hover:underline dark:text-blue-400"
        >
          Ver todas <ChevronRight className="size-3.5" />
        </button>
      </header>
      {isEmpty ? (
        <p class="rounded-md border border-dashed border-blue-500/20 bg-background/40 px-3 py-4 text-center text-[11px] text-muted-foreground">
          Sem mensagens novas.
        </p>
      ) : (
        <ul class="space-y-1.5">
          {recent.map((m) => (
            <li
              key={m.id}
              onClick={() => openModal(m)}
              class="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-background/60 px-3 py-2 hover:bg-accent/50"
            >
              <span class="mt-0.5 inline-block size-2 shrink-0 rounded-full bg-blue-600" />
              <div class="min-w-0 flex-1">
                <div class="truncate text-xs font-semibold">{m.title}</div>
                <div class="mt-0.5 truncate text-[11px] text-muted-foreground">
                  {m.fromName} · {m.body}
                </div>
              </div>
              <span class="shrink-0 text-[10px] text-muted-foreground">
                {formatShort(m.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {openItem && (
        <MessageModal
          m={openItem}
          onClose={() => setOpenItem(null)}
          onUnread={() => {
            void setMessageRead(openItem.id, false);
            setOpenItem(null);
          }}
        />
      )}
    </section>
  );
}

function formatShort(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "agora";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}
